import { getData, postData } from './base.service';

// Key documents of a reference (dbo.reference_documents). Only the link to the SharePoint file is stored.

export const DOC_TYPE_OPTIONS = [
    { value: 'tender_tor', label: 'Tender / ToR' },
    { value: 'technical_proposal', label: 'Technical proposal' },
    { value: 'financial_proposal', label: 'Financial proposal' },
    { value: 'contract', label: 'Contract' },
    { value: 'inception_report', label: 'Inception report' },
    { value: 'progress_report', label: 'Progress report' },
    { value: 'annual_report', label: 'Annual report' },
    { value: 'final_report', label: 'Final report' },
    { value: 'reference_form', label: 'Reference form' },
    { value: 'other', label: 'Other' },
];

export type ReferenceDocumentType = {
    id: number;
    refId: number;
    docType: string;
    otherLabel: string | null;
    title: string | null;
    fileName: string;
    driveId: string;
    itemId: string;
    webUrl: string | null;
    docDate: string | null;
    language: string | null;
    isFinal: boolean | null;
    summary: string | null;
};

export type ResolvedSharePointFile = { driveId: string; itemId: string; fileName: string; webUrl: string };

export type ReferenceDocumentFields = {
    docType: string;
    otherLabel: string | null;
    title: string | null;
    docDate: string | null;
    language: string | null;
    isFinal: boolean | null;
    summary: string | null;
};

export const getReferenceDocuments = (refId: number) =>
    getData(`references/documents/${refId}`) as Promise<ReferenceDocumentType[]>;

export const addReferenceDocument = (refId: number, file: ResolvedSharePointFile, fields: ReferenceDocumentFields) =>
    postData('references/documents/add', { refId, ...file, ...fields }) as Promise<ReferenceDocumentType>;

// `file` is only sent when the user linked a different SharePoint file.
export const updateReferenceDocument = (id: number, fields: ReferenceDocumentFields, file?: ResolvedSharePointFile) =>
    postData('references/documents/update', { id, ...(file || {}), ...fields }) as Promise<ReferenceDocumentType>;

// Removes only the catalogue row; the file on SharePoint is not touched.
export const removeReferenceDocument = (id: number) => postData('references/documents/remove', { id });

// shareId = 'u!' + base64url(url) without padding (Graph "shares" encoding).
export const encodeShareId = (url: string): string => {
    const bytes = new TextEncoder().encode(url);
    let binary = '';
    bytes.forEach((b) => { binary += String.fromCharCode(b); });
    return 'u!' + btoa(binary).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
};

// Resolves a pasted SharePoint link with the user's Graph token (the same token the app stores at login
// and uses in projectFiles.service.ts). Throws a readable error if the file cannot be resolved.
export async function resolveSharePointLink(rawUrl: string): Promise<ResolvedSharePointFile> {
    const url = rawUrl.trim();
    if (!/^https?:\/\//i.test(url)) throw new Error('Please paste a full SharePoint link (starting with https://).');
    const token = localStorage.getItem('accessToken') || '';
    if (!token) throw new Error('You are not signed in to Microsoft Graph. Please sign in again.');

    let response: Response;
    try {
        response = await fetch(`https://graph.microsoft.com/v1.0/shares/${encodeShareId(url)}/driveItem`, {
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch {
        throw new Error('Could not reach SharePoint. Please check your connection and try again.');
    }
    if (!response.ok) {
        if (response.status === 401) throw new Error('Your Microsoft session has expired. Please sign in again.');
        if (response.status === 403 || response.status === 404) {
            throw new Error('Could not open this link. Check that it is a SharePoint file link and that you have access to the file.');
        }
        throw new Error(`The link could not be resolved (error ${response.status}). Is it a SharePoint file link?`);
    }
    const item = await response.json();
    if (item.folder) throw new Error('This link points to a folder. Please link a file.');
    const driveId = item?.parentReference?.driveId;
    if (!driveId || !item?.id || !item?.name) throw new Error('The link did not resolve to a SharePoint file.');
    return { driveId, itemId: item.id, fileName: item.name, webUrl: item.webUrl || url };
}
